import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Calendar, Clock, Star } from "lucide-react";

interface Categoria {
  id: string;
  nome: string;
}

interface PublishOptionsProps {
  publicado: boolean;
  onPublicadoChange: (value: boolean) => void;
  destaque: boolean;
  onDestaqueChange: (value: boolean) => void;
  dataPublicacao: string;
  onDataPublicacaoChange: (value: string) => void;
  categoriaId: string;
  onCategoriaChange: (value: string) => void;
  categorias: Categoria[];
  statusAprovacao?: string;
}

export function PublishOptions({
  publicado,
  onPublicadoChange,
  destaque,
  onDestaqueChange,
  dataPublicacao,
  onDataPublicacaoChange,
  categoriaId,
  onCategoriaChange,
  categorias,
  statusAprovacao = "rascunho",
}: PublishOptionsProps) {
  const now = new Date();
  const pubDate = dataPublicacao ? new Date(dataPublicacao) : now;
  const isAgendado = publicado && pubDate > now;
  const isPublicado = publicado && pubDate <= now;
  const isApproved = statusAprovacao === "aprovado";
  const isAwaitingApproval = statusAprovacao === "aguardando_aprovacao";

  const getStatusBadge = () => {
    if (isAwaitingApproval) {
      return <Badge variant="secondary" className="bg-amber-100 text-amber-800">Aguardando Aprovação</Badge>;
    }
    if (!isApproved && !publicado) {
      return <Badge variant="outline">Rascunho</Badge>;
    }
    if (isApproved && !publicado) {
      return <Badge variant="default" className="bg-green-100 text-green-800">Aprovado</Badge>;
    }
    if (isAgendado) {
      return <Badge variant="secondary">Agendado</Badge>;
    }
    return <Badge variant="default">Publicado</Badge>;
  };

  return (
    <div className="space-y-6">
      {/* Status Badge */}
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-foreground">Status</span>
        {getStatusBadge()}
      </div>

      {/* Categoria */}
      <div className="space-y-2">
        <Label htmlFor="categoria">Categoria</Label>
        <Select value={categoriaId} onValueChange={onCategoriaChange}>
          <SelectTrigger id="categoria">
            <SelectValue placeholder="Selecione uma categoria" />
          </SelectTrigger>
          <SelectContent>
            {categorias.map((cat) => (
              <SelectItem key={cat.id} value={cat.id}>
                {cat.nome}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Data de Publicação */}
      <div className="space-y-2">
        <Label htmlFor="data-publicacao" className="flex items-center gap-2">
          <Calendar className="h-4 w-4" />
          Data de Publicação
        </Label>
        <Input
          id="data-publicacao"
          type="datetime-local"
          value={dataPublicacao ? dataPublicacao.slice(0, 16) : ""}
          onChange={(e) => {
            const value = e.target.value;
            if (value) {
              onDataPublicacaoChange(new Date(value).toISOString());
            }
          }}
        />
        {isAgendado && (
          <p className="text-xs text-muted-foreground flex items-center gap-1">
            <Clock className="h-3 w-3" />
            Será publicado automaticamente na data selecionada
          </p>
        )}
      </div>

      {/* Switches */}
      <div className="space-y-4 pt-2">
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label htmlFor="publicado" className={`cursor-pointer ${!isApproved ? "text-muted-foreground" : ""}`}>
              Publicar
            </Label>
            <p className="text-xs text-muted-foreground">
              {isApproved 
                ? "Tornar o artigo visível no blog" 
                : "Requer aprovação antes de publicar"}
            </p>
          </div>
          <Switch
            id="publicado"
            checked={publicado}
            onCheckedChange={onPublicadoChange}
            disabled={!isApproved}
          />
        </div>

        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label htmlFor="destaque" className="cursor-pointer flex items-center gap-1">
              <Star className="h-4 w-4 text-yellow-500" />
              Destaque
            </Label>
            <p className="text-xs text-muted-foreground">
              Exibir no topo do blog
            </p>
          </div>
          <Switch
            id="destaque"
            checked={destaque}
            onCheckedChange={onDestaqueChange}
          />
        </div>
      </div>
    </div>
  );
}
